import { useEffect, useRef } from "react";
import { useLocation } from "react-router";

import { sendPageView } from "@/lib/api";
import { logger } from "@/lib/logger";
import { PageTimer, secondsToReport } from "@/lib/page-time";

/**
 * Report how long each page was visible, for the admin Usage page.
 *
 * Mounted once, by `<PageTimeTracker>` in the authenticated shell — never by
 * the shell itself: this reads the location, so whatever calls it re-renders
 * on every navigation. A stretch is sent when the person
 * moves to another route, and when they hide the tab — the last moment a
 * browser reliably runs code, since `pagehide`/`unload` are often skipped on
 * mobile. Only the path is sent (never the query string, which can carry
 * what someone searched for); the server collapses ids out of it as well.
 * Whether a stretch is sent at all, and as how many seconds, is
 * `secondsToReport`'s decision.
 */
export function usePageTimeTracking(): void {
  const { pathname } = useLocation();
  const current = useRef<PageTimer | null>(null);

  useEffect(() => {
    const timer = new PageTimer(pathname, performance.now(), isVisible());
    current.current = timer;
    return () => {
      flush(timer);
      current.current = null;
    };
  }, [pathname]);

  useEffect(() => {
    function onVisibilityChange() {
      const timer = current.current;
      if (timer === null) {
        return;
      }
      const now = performance.now();
      if (isVisible()) {
        timer.show(now);
      } else {
        timer.hide(now);
        flush(timer);
      }
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
}

function isVisible(): boolean {
  return document.visibilityState === "visible";
}

function flush(timer: PageTimer): void {
  const seconds = secondsToReport(timer.take(performance.now()));
  if (seconds === null) {
    return;
  }
  logger.debug("[usage] page time", timer.path, seconds);
  sendPageView({ path: timer.path, view_id: timer.viewId, seconds });
}
